/**
 * ============================================================================
 * USE CLICK OUTSIDE HOOK
 * ============================================================================
 * 
 * Custom hook for detecting clicks outside an element
 */

import { useEffect, useRef } from 'react';

/**
 * Click outside hook
 * @param {Function} handler - Callback function when click outside occurs
 * @param {boolean} enabled - Whether the listener is enabled
 * @returns {Object} Ref to attach to the element
 */
export const useClickOutside = (handler, enabled = true) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return;

    const handleClickOutside = (event) => {
      // Do nothing if clicking ref's element or descendent elements
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }

      handler(event);
    };

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handler, enabled]);

  return ref;
};

export default useClickOutside;
